import { Checkbox } from '@/components/ui/shadcn/checkbox'
import { cn } from '@/lib/utils'
import {
  AUTOMATION_PLATFORMS,
  type AutomationPlatform,
  type DayOfWeek,
} from '@backend/lib/db/schema/automation'
import type { DraftSchedule } from '@/stores/companySetup/companySetupStore'

const DAY_META: { key: DayOfWeek; label: string; short: string }[] = [
  { key: 'monday', label: 'Monday', short: 'Mon' },
  { key: 'tuesday', label: 'Tuesday', short: 'Tue' },
  { key: 'wednesday', label: 'Wednesday', short: 'Wed' },
  { key: 'thursday', label: 'Thursday', short: 'Thu' },
  { key: 'friday', label: 'Friday', short: 'Fri' },
  { key: 'saturday', label: 'Saturday', short: 'Sat' },
  { key: 'sunday', label: 'Sunday', short: 'Sun' },
]

const PLATFORM_META: { key: AutomationPlatform; label: string }[] = [
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'facebook', label: 'Facebook' },
]

interface Props {
  schedule: DraftSchedule
  onScheduleChange: (schedule: DraftSchedule) => void
  sendReminders: boolean
  onSendRemindersChange: (value: boolean) => void
}

export function CompanySetupSchedulePicker({
  schedule,
  onScheduleChange,
  sendReminders,
  onSendRemindersChange,
}: Props) {
  const toggleDay = (day: DayOfWeek) => {
    const current = schedule[day]
    if (current && current.length > 0) {
      const { [day]: _, ...rest } = schedule
      onScheduleChange(rest)
    } else {
      onScheduleChange({ ...schedule, [day]: [...AUTOMATION_PLATFORMS] })
    }
  }

  const togglePlatform = (day: DayOfWeek, platform: AutomationPlatform) => {
    const current = schedule[day] || []
    const has = current.includes(platform)
    const updated = has ? current.filter((p) => p !== platform) : [...current, platform]
    if (updated.length === 0) {
      const { [day]: _, ...rest } = schedule
      onScheduleChange(rest)
    } else {
      onScheduleChange({ ...schedule, [day]: updated })
    }
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {DAY_META.map(({ key, label, short }) => {
          const activePlatforms = schedule[key] || []
          const isActive = activePlatforms.length > 0

          return (
            <div
              key={key}
              className={cn(
                'flex items-center gap-3 rounded-lg border px-4 py-3 transition-colors',
                isActive ? 'border-primary/30 bg-primary/5' : 'border-border bg-background',
              )}>
              <button
                type="button"
                onClick={() => toggleDay(key)}
                className={cn(
                  'w-10 shrink-0 rounded-md py-1 text-center text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'border-border bg-background text-foreground hover:bg-hover border',
                )}>
                {short}
              </button>

              <span
                className={cn(
                  'hidden w-20 text-sm sm:block',
                  isActive ? 'text-foreground font-medium' : 'text-muted-foreground',
                )}>
                {label}
              </span>

              {isActive && (
                <div className="flex gap-2">
                  {PLATFORM_META.map(({ key: platformKey, label: platformLabel }) => {
                    const selected = activePlatforms.includes(platformKey)
                    return (
                      <button
                        key={platformKey}
                        type="button"
                        onClick={() => togglePlatform(key, platformKey)}
                        className={cn(
                          'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                          selected
                            ? 'bg-primary text-primary-foreground'
                            : 'border-border bg-background text-foreground hover:bg-hover border',
                        )}>
                        {platformLabel}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>

      <div className="space-y-1">
        <div className="flex items-center gap-3">
          <Checkbox
            id="send-reminders"
            checked={sendReminders}
            onCheckedChange={(checked) => onSendRemindersChange(checked === true)}
          />
          <label htmlFor="send-reminders" className="text-foreground text-sm font-medium">
            Send me reminders with post ideas
          </label>
        </div>
        <p className="text-muted-foreground ml-7 text-sm">
          We'll email you when a new post is ready on your scheduled days.
        </p>
      </div>
    </div>
  )
}
